import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Copy, Plus, Router as RouterIcon, Terminal, Trash2 } from 'lucide-react';
import type { Organization, Page, Site, SiteProvisioning, TunnelType } from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatBytes, formatRelative, formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

const STATUS_TONE = { up: 'success', down: 'danger', pending: 'warn' } as const;

export function GatewaysPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, activeOrg, can } = useSession();
  const [showForm, setShowForm] = useState(false);
  const [provisioning, setProvisioning] = useState<SiteProvisioning | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    tunnelType: 'wg' as TunnelType,
    managedSubnets: '',
    publicEndpoint: '',
    orgId: '',
    // On by default (2026-09-17): the tenant's ranges stay off the tunnel.
    addressTranslation: true,
  });
  // A superadmin working platform-wide has no implied tenant, so the gateway
  // needs one picked explicitly.
  const needsOrgChoice = session?.user.role === 'superadmin' && !activeOrg;

  const sitesQuery = useQuery({
    queryKey: ['sites', activeOrg?.id ?? 'all'],
    queryFn: () => api.get<Page<Site>>('/sites'),
    refetchInterval: 15_000,
  });

  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: () => api.get<Page<Organization>>('/orgs'),
    enabled: needsOrgChoice,
  });

  const createSite = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<Site>('/sites', input),
    onSuccess: async (site) => {
      setShowForm(false);
      setForm({ name: '', tunnelType: 'wg', managedSubnets: '', publicEndpoint: '', orgId: '', addressTranslation: true });
      setFormError(null);
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      // A new gateway is useless without its router script, so show it straight away.
      provision.mutate(site.id);
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Could not create gateway'),
  });

  const provision = useMutation({
    mutationFn: (siteId: string) =>
      api.post<SiteProvisioning>(`/sites/${encodeURIComponent(siteId)}/provisioning`),
    onSuccess: (data) => setProvisioning(data),
  });

  const removeSite = useMutation({
    mutationFn: (siteId: string) => api.delete(`/sites/${encodeURIComponent(siteId)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] }),
  });

  const sites = sitesQuery.data?.items ?? [];
  const counts = {
    total: sites.length,
    up: sites.filter((s) => s.status === 'up').length,
    down: sites.filter((s) => s.status === 'down').length,
    pending: sites.filter((s) => s.status === 'pending').length,
  };

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    createSite.mutate({
      name: form.name.trim(),
      vendor: 'mikrotik',
      tunnelType: form.tunnelType,
      // The platform alone chooses; a tenant's gateway is created translated
      // and the field is never sent (the API would refuse it by name).
      ...(can('org.manage') ? { addressTranslation: form.addressTranslation ? 'netmap' : 'none' } : {}),
      managedSubnets: form.managedSubnets
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      ...(form.publicEndpoint.trim() ? { publicEndpoint: form.publicEndpoint.trim() } : {}),
      ...(needsOrgChoice && form.orgId ? { orgId: form.orgId } : {}),
    });
  }

  return (
    <>
      <PageHeader
        title="Gateways"
        description="Site routers carrying the management plane to their CPEs over WireGuard or OpenVPN."
        actions={
          can('site.manage') ? (
            <Button
              variant="primary"
              icon={<Plus className="size-4" />}
              onClick={() => setShowForm((v) => !v)}
            >
              New gateway
            </Button>
          ) : null
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Gateways" value={counts.total} icon={<RouterIcon className="size-4" />} />
        <StatCard label="Up" value={counts.up} tone="success" />
        <StatCard label="Down" value={counts.down} tone={counts.down ? 'danger' : 'default'} />
        <StatCard label="Awaiting router" value={counts.pending} tone={counts.pending ? 'warn' : 'default'} />
      </div>

      {showForm && (
        <Card title="New gateway" className="mt-5">
          <form onSubmit={onSubmit} className="grid gap-4 p-5 md:grid-cols-2">
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="MConnect FTTH — Gachie"
                required
              />
            </Field>
            <Field label="Tunnel">
              <select
                className="input"
                value={form.tunnelType}
                onChange={(e) => setForm({ ...form, tunnelType: e.target.value as TunnelType })}
              >
                <option value="wg">WireGuard</option>
                <option value="ovpn">OpenVPN</option>
              </select>
            </Field>
            <Field
              label="CPE management ranges"
              hint="Comma separated CIDRs reachable behind this router, e.g. 10.10.0.0/16"
            >
              <Input
                value={form.managedSubnets}
                onChange={(e) => setForm({ ...form, managedSubnets: e.target.value })}
                placeholder="10.10.0.0/16"
                required
              />
            </Field>
            <Field label="Router public address" hint="Optional, for your own reference">
              <Input
                value={form.publicEndpoint}
                onChange={(e) => setForm({ ...form, publicEndpoint: e.target.value })}
                placeholder="102.215.34.8"
              />
            </Field>
            {can('org.manage') && (
              // Platform only (2026-09-18): a tenant never sees the option — their
              // gateway is translated by default and that is the platform's business.
              <Field
                label="Address translation"
                hint="The platform routes a block of its own to this gateway and the router maps the tenant's ranges onto it one-to-one, so their addressing stays theirs and another organization may use the same ranges. Untick only for a router that cannot NAT: its ranges are then routed as they are and must be free platform-wide."
              >
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.addressTranslation}
                    onChange={(e) => setForm({ ...form, addressTranslation: e.target.checked })}
                  />
                  Translate addresses on the tunnel (recommended)
                </label>
              </Field>
            )}
            {needsOrgChoice && (
              <Field label="Organization" hint="Which tenant this gateway belongs to">
                <select
                  className="input"
                  value={form.orgId}
                  onChange={(e) => setForm({ ...form, orgId: e.target.value })}
                  required
                >
                  <option value="">Select…</option>
                  {orgsQuery.data?.items.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div className="md:col-span-2">
              {formError && <ErrorNotice error={new Error(formError)} />}
              <div className="mt-3 flex gap-2">
                <Button type="submit" variant="primary" loading={createSite.isPending}>
                  Create and generate script
                </Button>
                <Button type="button" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </Card>
      )}

      {provisioning && (
        <ProvisioningPanel provisioning={provisioning} onClose={() => setProvisioning(null)} />
      )}

      <Card className="mt-5">
        {sitesQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : sitesQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={sitesQuery.error} />
          </div>
        ) : sites.length === 0 ? (
          <EmptyState
            icon={<RouterIcon className="size-8" />}
            title="No gateways yet"
            description="Add the site router that fronts your ONUs, then run the generated script on it."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">#</th>
              <th className="th">Status</th>
              <th className="th">Name</th>
              <th className="th">Tunnel</th>
              <th className="th">Tunnel IP</th>
              <th className="th">Connected subnets</th>
              <th className="th">ONUs</th>
              <th className="th">Traffic</th>
              <th className="th">Last handshake</th>
              <th className="th" />
            </THead>
            <TBody>
              {sites.map((site) => (
                <TRow
                  key={site.id}
                  onClick={() => navigate(`/gateways/${site.number ?? site.id}`)}
                >
                  <td className="td text-sm tabular-nums text-muted">#{site.number ?? '—'}</td>
                  <td className="td">
                    <Pill state tone={STATUS_TONE[site.status]} dot>
                      {site.status === 'pending' ? 'awaiting router' : site.status}
                    </Pill>
                  </td>
                  <td className="td font-medium">{site.name}</td>
                  <td className="td">
                    <Pill tone={site.tunnelType === 'wg' ? 'primary' : 'info'}>
                      {site.tunnelType === 'wg' ? 'WireGuard' : 'OpenVPN'}
                    </Pill>
                  </td>
                  <td className="td font-mono text-xs">{site.tunnelIp ?? '—'}</td>
                  <td className="td">
                    <div className="flex flex-wrap gap-1">
                      {site.managedSubnets.length === 0 ? (
                        <Pill tone="warn">none routed</Pill>
                      ) : (
                        site.managedSubnets.map((subnet) => (
                          <span
                            key={subnet.cidr}
                            className="inline-flex items-center gap-1.5 rounded border border-line bg-surface-2 px-1.5 py-0.5 text-[11px]"
                            title={subnet.label ?? undefined}
                          >
                            {subnet.label && <span className="text-ink">{subnet.label}</span>}
                            <span className="font-mono text-muted">{subnet.cidr}</span>
                            {can('org.manage') && subnet.mapped && (
                              <span className="font-mono text-faint" title="The platform block the tunnel routes for this range">
                                → {subnet.mapped}
                              </span>
                            )}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="td tabular-nums">{site.deviceCount ?? 0}</td>
                  <td className="td text-xs text-muted">
                    ↓ {formatBytes(site.rxBytes)} · ↑ {formatBytes(site.txBytes)}
                  </td>
                  <td className="td text-muted">{formatRelative(site.lastHandshakeAt)}</td>
                  <td className="td" onClick={(e) => e.stopPropagation()}>
                    {can('site.manage') && (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          icon={<Terminal className="size-3.5" />}
                          loading={provision.isPending && provision.variables === site.id}
                          onClick={() => provision.mutate(site.id)}
                        >
                          Script
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => {
                            if (
                              confirm(
                                `Delete ${site.name}? The tunnel drops and its ONUs become unreachable.`,
                              )
                            ) {
                              removeSite.mutate(site.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      icon={<Copy className="size-3.5" />}
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

function ProvisioningPanel({
  provisioning,
  onClose,
}: {
  provisioning: SiteProvisioning;
  onClose: () => void;
}) {
  return (
    <Card
      className="mt-5"
      title="Router provisioning"
      action={
        <Button size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4 p-5">
        <div>
          <span className="label">Run this on the router</span>
          <div className="flex gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-xs text-ink">
              {provisioning.fetchCommand}
            </code>
            <CopyButton value={provisioning.fetchCommand} label="Copy" />
          </div>
          <p className="mt-1 text-xs text-faint">
            The link is single-purpose and expires{' '}
            {provisioning.tokenExpiresAt
              ? formatTimestamp(provisioning.tokenExpiresAt)
              : 'when rotated'}
            . Generating a new script invalidates it.
          </p>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="label mb-0">Or paste the script directly</span>
            <CopyButton value={provisioning.script} label="Copy script" />
          </div>
          <pre className="max-h-96 overflow-auto rounded-lg border border-line bg-surface-2 p-4 font-mono text-[11px] leading-relaxed text-muted">
            {provisioning.script}
          </pre>
        </div>

        {provisioning.serverPublicKey && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <span className="label">ACS public key</span>
              <code className="block truncate rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-xs">
                {provisioning.serverPublicKey}
              </code>
            </div>
            <div>
              <span className="label">ACS endpoint</span>
              <code className="block rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-xs">
                {provisioning.serverEndpoint}
              </code>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
